// src/components/layouts/UserLayout.jsx
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import styles from './PrivateLayout.module.css';

export default function PrivateLayout() {
    const { user, logout } = useAuth();
    const role = user?.role ?? 'USER';
    const navigate = useNavigate();
    const loc = useLocation();

    // console.log('VERSION:', process.env.REACT_APP_VERSION);

    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const userMenuRef = useRef(null);

    const NAV_BY_ROLE = useMemo(
        () => ({
            PERSONAL: [
                { to: '/dashboard', label: '대시보드' },
                { to: '/service-board', label: '시뮬레이션' },
                { to: '/services-apply', label: '서비스관리' },
                { to: '/mypage', label: '계정관리' },
            ],
            USER: [
                { to: '/dashboard', label: '대시보드' },
                { to: '/service-board', label: '시뮬레이션' },
                { to: '/mypage', label: '계정관리' },
            ],
            ADMIN: [
                { to: '/dashboard', label: '대시보드' },
                { to: '/services-apply', label: '서비스관리' },
                { to: '/user-manager', label: '사용자관리' },
                { to: '/mypage', label: '계정관리' },
            ],
            SYSTEM: [
                { to: '/dashboard', label: '대시보드' },
                { to: '/serviceadmin', label: '서비스 관리' },
                { to: '/support/admin/notices', label: '공지사항 관리' },
                { to: '/support/admin/faqs', label: 'FAQ 관리' },
                { to: '/support/admin/inquiries', label: '1:1문의 관리' },
            ],
        }),
        []
    );

    const navItems = NAV_BY_ROLE[role] || NAV_BY_ROLE.USER;

    const displayName = useMemo(() => {
        if (!user) return '';
        if (typeof user === 'string') return user;
        return user.name || user.email || '사용자';
    }, [user]);

    const avatarChar = displayName?.[0]?.toUpperCase() || 'U';

    const handleLogout = (e) => {
        e.preventDefault();
        setUserMenuOpen(false);
        logout();
        navigate('/');
    };

    const handleAvatarClick = () => {
        setUserMenuOpen((open) => !open);
    };

    // 바깥 클릭 시 닫기
    useEffect(() => {
        const onClick = (e) => {
            if (!userMenuRef.current) return;
            if (!userMenuRef.current.contains(e.target)) {
                setUserMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    return (
        <div className={styles.uShell}>
            {/* 데스크톱 사이드바 */}
            <aside className={styles.uSidebar}>
                <NavLink to="/dashboard" className={styles.uLogo}>
                    FirST SIM
                </NavLink>
                <nav className={styles.uSideNav}>
                    {navItems.map((item) => (
                        <NavLink key={item.to} to={item.to} className={({ isActive }) => (isActive ? `${styles.uSideLink} ${styles.uSideLinkActive}` : styles.uSideLink)}>
                            {item.label}
                        </NavLink>
                    ))}
                </nav>
            </aside>

            <main className={styles.uMain}>
                {/* 상단: 타이틀 + (우측) 아바타 / 유저메뉴 */}
                <div className={styles.uTopbar}>
                    <div className={styles.uTopLeft}>
                        <h1 className={styles.uTitle}>
                            <span className={styles.uTitleDesktop}>화재 확산 및 피난경로 안내 웹 서비스</span>
                            <span className={styles.uTitleMobile}>
                                화재 확산 및 피난경로 <br />
                                안내 웹 서비스
                            </span>
                        </h1>
                    </div>

                    <div className={styles.uTopRight} ref={userMenuRef}>
                        <div className={styles.uUserWrap}>
                            {/* 아바타 버튼 (모바일: 이것만 보임) */}
                            <button
                                type="button"
                                className={styles.uUserAvatarBtn}
                                onClick={handleAvatarClick}
                                aria-haspopup="true"
                                aria-expanded={userMenuOpen}
                                aria-label="사용자 메뉴 열기"
                            >
                                <div className={styles.uUser__avatar}>{avatarChar}</div>
                            </button>

                            {/* 데스크톱: 항상 보이는 패널, 모바일: 드롭다운으로 토글 */}
                            <div className={userMenuOpen ? `${styles.uUserPanel} ${styles.uUserPanelOpen}` : styles.uUserPanel}>
                                <div className={styles.uUser__meta}>
                                    <div className={styles.uUser__name}>{user?.name || '사용자'}</div>
                                    <small style={{ opacity: 0.7 }}>{role}</small>
                                </div>
                                <button className={styles.uLinkLogout} onClick={handleLogout}>
                                    로그아웃
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 모바일 상단 탭 메뉴 */}
                <nav className={styles.uTopNav}>
                    {navItems.map((item) => (
                        <NavLink key={item.to} to={item.to} className={({ isActive }) => (isActive ? `${styles.uTopNavLink} ${styles.uTopNavLinkActive}` : styles.uTopNavLink)}>
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className={styles.uContent}>
                    <Outlet key={loc.pathname} />
                </div>

                <footer className={styles.footer}>COPYRIGHT (C) REALTIMETECH INC. ALL RIGHTS RESERVED. | v{process.env.REACT_APP_VERSION}</footer>
            </main>
        </div>
    );
}
